import { createSlice } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const Grids = {
    BIDS: 'bids',
    CLAIMS: 'claims',
    CONVERSATIONS: 'conversations',
    OPPORTUNITIES: 'opportunities',
    ORTF_INBOUND_FILES: 'ortfInboundFiles',
    ORTF_OUTBOUND_FILE_SCRIPTS: 'ortfOutboundFileScripts',
    ORTF_REMAINING_TRANSFER_SCRIPTS: 'ortfRemainingTransferScripts',
    REFERRALS: 'referrals',
    REQUESTS: 'requests',
    REQUESTS_RESOLVED: 'requestsResolved',
    TEST_CLAIMS: 'testClaims',
    TREATMENTS: 'treatments',
    TREATMENT_MERGE_CASES: 'treatmentMergeCases',
    UPCOMING_TREATMENTS: 'upcomingTreatments',
};

const initialGridState = {
    page: 0,
};

const initGridStates = () =>
    Object.fromEntries(Object.values(Grids).map((grid) => [grid, initialGridState]));

export const gridsSlice = createSlice({
    name: 'grids',
    initialState: initGridStates(),
    reducers: {
        setGridSort(state, action) {
            const { grid, sort } = action.payload;
            return { ...state, [grid]: { ...state[grid], sort } };
        },
        setGridSearch(state, action) {
            const { grid, search } = action.payload;
            return { ...state, [grid]: { ...state[grid], search } };
        },
        setGridActiveFilters(state, action) {
            const { grid, activeFilters } = action.payload;
            return { ...state, [grid]: { ...state[grid], activeFilters } };
        },
        setFilterPaneWidth(state, action) {
            const { grid, filterPaneWidth } = action.payload;
            return { ...state, [grid]: { ...state[grid], filterPaneWidth } };
        },
        setCurrentPage(state, action) {
            const { grid, page } = action.payload;
            return { ...state, [grid]: { ...state[grid], page } };
        },
    },
});

export const {
    setCurrentPage,
    setGridSort,
    setGridSearch,
    setGridActiveFilters,
    setFilterPaneWidth,
} = gridsSlice.actions;

export const useGridState = (grid, initialSortProperties) => {
    const dispatch = useDispatch();
    // Session-persisted grids state can predate a newly added Grids key.
    const { sort, search, activeFilters, filterPaneWidth, page } = useSelector(
        (state) => state.grids[grid] ?? initialGridState,
    );
    useEffect(() => {
        if (!sort) {
            dispatch(setGridSort({ grid, sort: initialSortProperties }));
        }
    }, [grid, sort, initialSortProperties, dispatch]);
    return {
        // The effect above only lands the initial sort on the second render, so fall back to it
        // here. Otherwise the first render queries unsorted and the sort re-triggers the request.
        sort: sort ?? initialSortProperties,
        search,
        activeFilters,
        filterPaneWidth,
        // Session-persisted grid blobs can omit page; GridWithFilters client-filters when
        // currentPage is not an integer, which breaks server-paged sentinel filters.
        page: page ?? 0,
        updateGridSort: (sortProperties) => dispatch(setGridSort({ grid, sort: sortProperties })),
        updateGridSearch: (searchText) => dispatch(setGridSearch({ grid, search: searchText })),
        updateGridActiveFilters: (filters) => {
            // Need to deep compare filter objects to avoid infinite rerender loop when setting initial filters
            // May want to look into limiting re-renders in internal component logic
            if (JSON.stringify(activeFilters) !== JSON.stringify(filters)) {
                dispatch(setGridActiveFilters({ grid, activeFilters: filters }));
            }
        },
        updateFilterPaneWidth: (width) =>
            dispatch(setFilterPaneWidth({ grid, filterPaneWidth: width })),
        updateCurrentPage: (currentPage) => dispatch(setCurrentPage({ grid, page: currentPage })),
    };
};
